import React, { createContext, useContext, useState, useMemo, useEffect } from 'react';
import { User } from '@supabase/supabase-js';
import Api from '../api';

interface AuthContextType {
    user: User | null;
    signIn: (email: string, password: string) => Promise<void>;
    signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const api = useMemo(() => new Api(), []);

    useEffect(() => {
        const checkUser = async () => {
            try {
                const currentUser = await api.getCurrentUser();
                setUser(currentUser);
            } catch (error) {
                setUser(null);
            }
        };

        checkUser();
    }, [api]);

    const signIn = async (email: string, password: string) => {
        const user = await api.signIn(email, password);
        setUser(user);
    };

    const signOut = async () => {
        await api.signOut();
        setUser(null);
    };

    const value = {
        user,
        signIn,
        signOut,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
